// Pure calendar logic for the engineer PWA "My calendar" screen (Session AFF, Loop C).
//
// Every function takes explicit Date / now parameters and works in LOCAL device time
// (the engineer's device is UK). Nothing here reads the clock or the DOM, so it is fully
// unit-testable and deterministic. The screen derives a job's colour from its live status
// on every render via calendarJobColour, so a job that completes (context reload) re-renders
// in the completed colour with no page refresh.

// The five locked job-status colours (see the Session AFF palette). A colour is always DERIVED
// from status via calendarJobColour, never stored.
export const CALENDAR_COLOURS = {
  // Maintenance (service / repair / loler) still scheduled.
  maintenance: "#2CB9F3", // blue
  // Call-out active (scheduled / in_progress callout).
  callout: "#b8860b", // amber
  // Any job completed.
  complete: "#63C41F", // green
  // Any job overdue / missed (scheduled before today, still open).
  overdue: "#c0392b", // red
  // Any job pending office approval.
  pending: "#7d21c7", // purple
} as const;

// The minimum shape calendarJobColour needs. JobView satisfies this.
export interface CalendarJobLike {
  type: string;
  status: string;
  scheduledFor: string | null;
}

// --- Local-day primitives -------------------------------------------------

// Midnight (00:00) on the local calendar day of `d`.
export function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

// True when a and b fall on the same local calendar day.
export function isSameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  );
}

// A stable "YYYY-MM-DD" key for a local calendar day (used for month-cell identity).
export function localDateKey(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

// Add `n` days, preserving local-midnight semantics across month / year / DST boundaries
// (constructs from local components rather than adding milliseconds).
export function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

// Move `n` whole months, anchored to the 1st of the target month (so Jan 31 + 1 month is
// Feb 1, never an overflow into March). Used for month-view navigation, where only the
// month / year matter.
export function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

// Monday (local midnight) of the week containing `anchor`.
export function startOfWeek(anchor: Date): Date {
  const d = startOfLocalDay(anchor);
  const day = d.getDay(); // 0 = Sunday .. 6 = Saturday
  const mondayOffset = day === 0 ? -6 : 1 - day;
  return addDays(d, mondayOffset);
}

// --- Views ----------------------------------------------------------------

// The seven Monday-to-Sunday local dates (each at local midnight) of the week containing `anchor`.
export function weekDays(anchor: Date): Date[] {
  const monday = startOfWeek(anchor);
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}

// One cell of the month grid.
export interface DayCell {
  date: Date; // local midnight
  inMonth: boolean; // false for the leading / trailing days that belong to the neighbouring month
}

// A Monday-aligned grid of weeks that fully covers `anchor`'s month. Returns 4 to 6 weeks,
// each an array of 7 cells; the first cell is always a Monday.
export function monthGrid(anchor: Date): DayCell[][] {
  const month = anchor.getMonth();
  const firstOfMonth = new Date(anchor.getFullYear(), month, 1);
  const lastOfMonth = new Date(anchor.getFullYear(), month + 1, 0);
  const gridStart = startOfWeek(firstOfMonth); // Monday on or before the 1st

  const weeks: DayCell[][] = [];
  let cursor = gridStart;
  // Build whole weeks until the last cell reaches or passes the final day of the month.
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const week: DayCell[] = [];
    for (let i = 0; i < 7; i++) {
      week.push({ date: cursor, inMonth: cursor.getMonth() === month && cursor.getFullYear() === anchor.getFullYear() });
      cursor = addDays(cursor, 1);
    }
    weeks.push(week);
    if (week[6].date.getTime() >= lastOfMonth.getTime()) break;
  }
  return weeks;
}

// --- Job placement --------------------------------------------------------

// Whether a job is currently overdue: scheduled strictly before today AND still open
// (scheduled | in_progress). A null / unparseable scheduledFor is never overdue.
export function isOverdue(job: CalendarJobLike, now: Date): boolean {
  if (!job.scheduledFor) return false;
  if (job.status !== "scheduled" && job.status !== "in_progress") return false;
  const d = new Date(job.scheduledFor);
  if (Number.isNaN(d.getTime())) return false;
  return startOfLocalDay(d).getTime() < startOfLocalDay(now).getTime();
}

// The locked colour for a job, derived from its live status. Precedence:
//   complete -> green; pending_approval -> purple; overdue -> red; callout -> amber; else blue.
export function calendarJobColour(job: CalendarJobLike, now: Date): string {
  if (job.status === "complete") return CALENDAR_COLOURS.complete;
  if (job.status === "pending_approval") return CALENDAR_COLOURS.pending;
  if (isOverdue(job, now)) return CALENDAR_COLOURS.overdue;
  if (job.type === "callout") return CALENDAR_COLOURS.callout;
  return CALENDAR_COLOURS.maintenance;
}

// Jobs scheduled on a given local day, earliest first. Jobs with a null / unparseable
// scheduledFor are excluded.
export function jobsOnDay<T extends { scheduledFor: string | null }>(jobs: T[], day: Date): T[] {
  return jobs
    .filter((j) => {
      if (!j.scheduledFor) return false;
      const d = new Date(j.scheduledFor);
      return !Number.isNaN(d.getTime()) && isSameLocalDay(d, day);
    })
    .sort((a, b) => new Date(a.scheduledFor as string).getTime() - new Date(b.scheduledFor as string).getTime());
}

// Open jobs (scheduled | in_progress) that carry no scheduled date, for the day view's
// "Unscheduled" section. Submitted / complete jobs are excluded (they are with the office).
export function unscheduledJobs<T extends { scheduledFor: string | null; status: string }>(jobs: T[]): T[] {
  return jobs.filter((j) => !j.scheduledFor && (j.status === "scheduled" || j.status === "in_progress"));
}

// The day view's hour rail: 06:00 to 21:00 inclusive (16 slots).
export interface HourSlot {
  hour: number;
  label: string;
}
export function hourSlots(): HourSlot[] {
  const slots: HourSlot[] = [];
  for (let h = 6; h <= 21; h++) {
    slots.push({ hour: h, label: `${String(h).padStart(2, "0")}:00` });
  }
  return slots;
}

// Bucket a day's jobs by their local hour for the day view, clamping anything before 06:00
// into the 06:00 slot and anything after 21:00 into the 21:00 slot so no job is ever hidden.
export function bucketJobsByHour<T extends { scheduledFor: string | null }>(dayJobs: T[]): Map<number, T[]> {
  const map = new Map<number, T[]>();
  for (const j of dayJobs) {
    if (!j.scheduledFor) continue;
    const d = new Date(j.scheduledFor);
    if (Number.isNaN(d.getTime())) continue;
    const h = Math.min(21, Math.max(6, d.getHours()));
    const arr = map.get(h) ?? [];
    arr.push(j);
    map.set(h, arr);
  }
  return map;
}
